import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';

// Components
import TableList from './Components/TableList/TableList';
import TableDisplay from './Components/TableView/TableDisplay';
import RowForm from './Forms/RowForm/RowForm';
import TableBuilder from './Forms/TableForm/TableBuilder';
import { FormModal, TableModal } from './Modals/BaseModal/BaseModal';
import { ErrorMessage, SuccessMessage } from './Common/ErrorMessage/ErrorMessage';

// Hooks
import { useTableData } from './Hooks/useTableData';
import { useTableOperations } from './Hooks/useTableOperations';
import { usePermissions } from './Hooks/usePermissions';

// Services
import TemplateService from '../../../AppSettings/services/TemplateService';
import TablesService from '../../../AppSettings/services/TablesService';

// Utils
import { TABLE_TYPES, DEFAULT_TEMPLATE_ROW, MESSAGES } from './Utils/constants';

// Styles
import './TemplateEdit.css';

/**
 * Main Template Edit Component
 * Orchestrates all template and table management functionality
 */
const TemplateEdit = ({ permishId }) => {
  // Hooks
  const permissions = usePermissions(permishId);
  const {
    allTables,
    selectedTable,
    selectedTableData,
    errorMessage,
    isLoading,
    error,
    handleTableClick,
    refreshTableData,
    closeTable,
    setErrorMessage,
    fetchCustomTables
  } = useTableData();

  const tableOperations = useTableOperations(
    selectedTable,
    selectedTableData,
    refreshTableData,
    setErrorMessage
  );

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isTableFormOpen, setIsTableFormOpen] = useState(false);
  const [isRowFormOpen, setIsRowFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [rowFormData, setRowFormData] = useState(null);

  // Row operations for template tables
  const handleTemplateRowAdd = () => {
    if (permissions.isRestricted) return;
    setEditingRow(null);
    setRowFormData({ ...DEFAULT_TEMPLATE_ROW });
    setIsRowFormOpen(true);
  };

  const handleTemplateRowEdit = (rowOrIdx) => {
    if (permissions.isRestricted) return;
    const rows = tableOperations.getCurrentRows();
    let row = rowOrIdx;
    
    if (typeof rowOrIdx === 'number') {
      row = rows[rowOrIdx];
    }
    
      setEditingRow(row);
    setRowFormData({
        equipment: row.equipment || "",
        ifb: row.ifb || "",
        ifc: row.ifc || "",
        notes: row.notes || "",
        ifr: row.ifr || "",
      });
      setIsRowFormOpen(true);
  };

  const handleRowFormSubmit = async (formData) => {
    try {
      if (editingRow) {
          // Include the row id when editing for templates
        const updatedRow = { ...formData, id: editingRow.id };
        await TemplateService.editRowInTemplate(selectedTable.table_name, updatedRow);
      } else {
        await TemplateService.addRowToTemplate(selectedTable.table_name, formData);
      }
      
      refreshTableData();
      setIsRowFormOpen(false);
      setEditingRow(null);
      setRowFormData(null);
    } catch (error) {
      console.error("Error saving row:", error);
      setErrorMessage(MESSAGES.ERRORS.SAVE_ROW);
    }
  };

  const handleRowFormCancel = () => {
    setIsRowFormOpen(false);
    setEditingRow(null);
    setRowFormData(null);
  };

  // Table creation
  const handleCreateTable = () => {
    if (permissions.isRestricted) return;
    setIsTableFormOpen(true);
  };

  const handleTableCreated = async () => {
    try {
      await fetchCustomTables();
      setSuccessMessage("Table created successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrorMessage(MESSAGES.ERRORS.REFRESH_TABLES);
    }
  };

  // Table deletion
  const handleDeleteCustomTable = async (tableId, tableName, event) => {
    event.stopPropagation();
    
    try {
      await TablesService.deleteCustomTable(tableId);
      await fetchCustomTables();
      setSuccessMessage(`${MESSAGES.SUCCESS.TABLE_DELETED} "${tableName}"`);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      const status = error?.status ?? error?.response?.status;
      const errorData = error?.data ?? error?.response?.data;
      const backendMessage = String(errorData?.error || errorData?.message || error?.message || '');
      const isConflictRequiringCascade =
        status === 409 ||
        backendMessage.toLowerCase().includes('cascade=true') ||
        backendMessage.toLowerCase().includes('being used in');

      if (isConflictRequiringCascade) {
        const holdingCount = errorData?.holding_count ?? 0;
        try {
          await TablesService.deleteCustomTable(tableId, true);
          await fetchCustomTables();
          setSuccessMessage(
            `${MESSAGES.SUCCESS.TABLE_DELETED_CASCADE} "${tableName}"${holdingCount ? ` and ${holdingCount} related record(s)` : ''}`
          );
          setTimeout(() => setSuccessMessage(""), 5000);
        } catch (cascadeError) {
          console.error('Error during cascade deletion:', cascadeError);
          const cascadeData = cascadeError?.data ?? cascadeError?.response?.data;
          const cascadeMessage =
            cascadeData?.error ||
            cascadeData?.message ||
            `Failed to delete custom table "${tableName}". Please try again.`;
          setErrorMessage(cascadeMessage);
          setTimeout(() => setErrorMessage(""), 3000);
        }
      } else {
        console.error('Error deleting custom table:', error);
        const errorMsg = errorData?.error || errorData?.message || `Failed to delete custom table "${tableName}". Please try again.`;
        setErrorMessage(errorMsg);
        setTimeout(() => setErrorMessage(""), 3000);
      }
    }
  };

  // Row operations (template tables only - custom tables use TableBuilder)
  const handleAddRow = handleTemplateRowAdd;
  const handleEditRow = handleTemplateRowEdit;
  const handleDeleteRow = tableOperations.handleDeleteRow;

  // Only show error if we can't load custom tables AND there's an error message
  // Don't block on checklist templates error since we only need custom tables
  if (error && errorMessage && allTables.length === 0) {
    return <div className="error-state">Error loading tables: {errorMessage}</div>;
  }

  return (
    <div className={`TE_Rule-Building-Container ${permissions.isRestricted ? 'TE_restricted-access' : ''}`}>
      {/* Messages */}
      {errorMessage && selectedTable && <ErrorMessage message={errorMessage} />}
      {successMessage && <SuccessMessage message={successMessage} />}
      
      {/* Restricted Access Overlay */}
      {permissions.isRestricted && (
        <div className="TE_restricted-overlay">
          <FontAwesomeIcon icon={faLock} size="2x" />
        </div>
      )}

      {/* Table List */}
      <TableList
        tables={allTables}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onTableClick={handleTableClick}
        onCreateTable={handleCreateTable}
        onDeleteTable={handleDeleteCustomTable}
        permissions={permissions}
        isLoading={isLoading}
        error={errorMessage}
      />

      {/* Table Edit Modal - Using TableBuilder for Excel-like editing */}
      {selectedTable && selectedTable._type === TABLE_TYPES.CUSTOM && (
        <TableModal onClose={closeTable}>
          <TableBuilder
            editMode={true}
            existingTable={selectedTableData}
            onClose={closeTable}
            onTableUpdated={async () => {
              await fetchCustomTables();
              setSuccessMessage("Table updated successfully!");
              setTimeout(() => setSuccessMessage(""), 3000);
              closeTable();
            }}
          />
        </TableModal>
      )}
      
      {/* Template Table View Modal - Simple read-only view with row actions */}
      {selectedTable && selectedTable._type === TABLE_TYPES.TEMPLATE && (
        <TableModal onClose={closeTable}>
          <TableDisplay
            table={selectedTable}
            rows={tableOperations.getCurrentRows()}
            onAddRow={handleAddRow}
            onEditRow={handleEditRow}
            onDeleteRow={handleDeleteRow}
            permissions={permissions}
          />
        </TableModal>
      )}

      {/* Row Form Modal (for template tables only - custom tables use full grid editing) */}
      {isRowFormOpen && selectedTable?._type !== TABLE_TYPES.CUSTOM && (
        <FormModal onClose={handleRowFormCancel}>
          <RowForm
            table={selectedTable}
            initialData={rowFormData}
            onSubmit={handleRowFormSubmit}
            onCancel={handleRowFormCancel}
          />
        </FormModal>
      )}

      {/* Table Creation Modal */}
      {isTableFormOpen && (
        <TableModal onClose={() => setIsTableFormOpen(false)}>
          <TableBuilder
          onClose={() => setIsTableFormOpen(false)}
          onTableCreated={handleTableCreated}
        />
        </TableModal>
      )}
    </div>
  );
};

export default TemplateEdit;